import React, { useEffect, useState, useCallback } from "react";
import { Table, Pagination } from "rsuite";
import { fetchServices } from "../../../api/servicesAPI";
import ImageModal from "../../imageModal";
import ReadMoreModal from "../../readMoreModal";
import { handleDeleteService } from "./deleteServiceHandler";
import { placeholderImage, isValidUrl } from "../../invalidImage";
import AdminPageHeader from "../_shared/AdminPageHeader";
import AdminSearchBar from "../_shared/AdminSearchBar";
import AdminCard from "../_shared/AdminCard";
import { EditButton, DeleteButton } from "../_shared/AdminActions";
import ServiceEditor from "./ServiceEditor";

const { Column, HeaderCell, Cell } = Table;

// ── View modes ────────────────────────────────────────────────
const VIEW = {
  LIST: "list",
  ADD:  "add",
  EDIT: "edit",
};

const ServiceTable = () => {
  const [view, setView]               = useState(VIEW.LIST);
  const [editingService, setEditingService] = useState(null);

  const [allData, setAllData]         = useState([]);
  const [data, setData]               = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [page, setPage]               = useState(1);
  const [limit, setLimit]             = useState(10);
  const [searchKeyword, setSearchKeyword] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const servicesData = await fetchServices();
      setAllData(servicesData);
      setData(servicesData);
    } catch (err) {
      console.error("Failed to fetch services:", err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const start = (page - 1) * limit;
    setDisplayData(data.slice(start, start + limit));
  }, [page, limit, data]);

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setPage(1);
    if (value) {
      setData(allData.filter((s) => s.name.toLowerCase().includes(value.toLowerCase())));
    } else {
      setData(allData);
    }
  };

  const handleEditorSuccess = () => {
    setView(VIEW.LIST);
    setEditingService(null);
    fetchData();
  };

  // ── Full-page editor view ─────────────────────────────────
  if (view === VIEW.ADD) {
    return (
      <div className="flex-1 min-h-screen">
        <ServiceEditor
          onSuccess={handleEditorSuccess}
          onCancel={() => setView(VIEW.LIST)}
        />
      </div>
    );
  }

  if (view === VIEW.EDIT && editingService) {
    return (
      <div className="flex-1 min-h-screen">
        <ServiceEditor
          service={editingService}
          onSuccess={handleEditorSuccess}
          onCancel={() => { setView(VIEW.LIST); setEditingService(null); }}
        />
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────
  return (
    <div className="flex-1 min-h-screen bg-slate-50 overflow-y-auto">
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <AdminPageHeader
          title="Service Management"
          subtitle="Kelola daftar layanan yang ditawarkan Defra"
          badge={data.length}
          actionLabel="➕ Tambah Service"
          onAction={() => setView(VIEW.ADD)}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
        />

        <AdminCard>
          <AdminSearchBar
            value={searchKeyword}
            onChange={handleSearch}
            placeholder="Cari berdasarkan nama layanan..."
          />

          <div className="overflow-hidden rounded-2xl border border-slate-100">
            <Table autoHeight data={displayData} rowHeight={100}>
              <Column width={60} align="center" fixed>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">No</HeaderCell>
                <Cell>
                  {(rowData, rowIndex) => (
                    <span className="text-sm font-semibold text-slate-500">
                      {(page - 1) * limit + rowIndex + 1}
                    </span>
                  )}
                </Cell>
              </Column>

              <Column width={100}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Image</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <ImageModal imageUrl={isValidUrl(rowData.image) ? rowData.image : placeholderImage} />
                  )}
                </Cell>
              </Column>

              <Column width={280}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Nama</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <span className="font-semibold text-slate-800">{rowData.name}</span>
                  )}
                </Cell>
              </Column>

              <Column flexGrow={1} minWidth={300}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Deskripsi</HeaderCell>
                <Cell>
                  {(rowData) => {
                    const text = rowData.description || "";
                    const truncated = text.length > 100 ? text.slice(0, 100) + "..." : text;
                    return (
                      <ReadMoreModal text={text}>
                        <span className="text-sm text-slate-600">{truncated}</span>
                      </ReadMoreModal>
                    );
                  }}
                </Cell>
              </Column>

              <Column width={200}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Aksi</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <div className="flex items-center gap-2">
                      <EditButton
                        onClick={() => {
                          setEditingService(rowData);
                          setView(VIEW.EDIT);
                        }}
                      />
                      <DeleteButton onClick={() => handleDeleteService(rowData.id, data, setData)} />
                    </div>
                  )}
                </Cell>
              </Column>
            </Table>
          </div>

          <div className="flex justify-end mt-5">
            <Pagination
              prev next first last ellipsis boundaryLinks
              maxButtons={5} size="xs"
              layout={["total", "-", "limit", "|", "pager"]}
              total={data.length}
              limitOptions={[5, 10, 20, 50]}
              limit={limit}
              activePage={page}
              onChangePage={setPage}
              onChangeLimit={(l) => { setLimit(l); setPage(1); }}
            />
          </div>
        </AdminCard>
      </div>
    </div>
  );
};

export default ServiceTable;
